import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { EnumeratedIconikAsset } from "@/components/iconik/types";

/** Where the preview was opened from: a chat play's accordion, or the Cart. */
export type ClipPreviewSource = "chat" | "cart";

/**
 * One group of clips in the answer, as a step the preview can navigate to.
 *
 * A group is a single accordion in the chat: one play's camera angles, or one
 * multi-sport asset. The preview still holds exactly one group's clips at a
 * time — ``clips`` is never flattened across groups, so the counter, the
 * footer and the panel's bulk actions all keep their per-group meaning. This
 * sequence is only what lets the arrows step *between* groups once the user
 * reaches the first or last clip of the one they are on.
 *
 * Clips are resolved lazily through ``load`` rather than carried here. The
 * loaders it wraps are cache-backed, so a group whose accordion is on screen
 * resolves from memory without a request; one that somehow is not cached is
 * fetched on demand. Carrying resolved clips instead would mean the answer's
 * every group had to be loaded before the first arrow press, and would leave
 * the multi-sport groups — which are not prefetched — out of the sequence.
 */
export interface PreviewGroup {
  /** Stable identity of the group, matched against {@link OpenPreviewArgs.groupKey}. */
  key: string;
  gameId: string;
  playId: string;
  /** Resolve this group's clips. Expected to be cache-backed and cheap. */
  load: () => Promise<EnumeratedIconikAsset[]>;
}

export interface OpenPreviewArgs {
  /** All clips for the play whose accordion the user is interacting with. */
  clips: EnumeratedIconikAsset[];
  /** Index (within ``clips``) of the clip to show first. */
  index: number;
  gameId: string;
  playId: string;
  /** Defaults to "chat". "cart" switches the panel's bulk actions to the cart. */
  source?: ClipPreviewSource;
  /**
   * Every group in the answer this preview was opened from, in the order they
   * appear on screen. Supplied by the answer that owns the accordions, so the
   * sequence travels with the click rather than living in a global registry —
   * a chat holds several answers, each with its own groups, and whichever one
   * the user clicked into is the one the arrows should walk.
   *
   * Omitted (the Cart, or any caller that has no sequence) leaves navigation
   * confined to ``clips``, exactly as before.
   */
  sequence?: PreviewGroup[];
  /** Key (within ``sequence``) of the group ``clips`` came from. */
  groupKey?: string;
}

export interface ClipPreviewContextType {
  /** Whether the Clip Preview panel should occupy the split panel. */
  isOpen: boolean;
  clips: EnumeratedIconikAsset[];
  activeIndex: number;
  activeClip: EnumeratedIconikAsset | undefined;
  gameId: string;
  playId: string;
  /** Where the current preview was opened from. Drives cart-scoped actions. */
  source: ClipPreviewSource;
  /**
   * Whether {@link ClipPreviewContextType.next} has anywhere to go: another
   * clip in this group, or a later group in the sequence. The panel's arrow
   * reads this instead of comparing against ``clips.length``, which only knows
   * about the group currently loaded.
   */
  hasNext: boolean;
  /** Mirror of {@link ClipPreviewContextType.hasNext} for ``prev``. */
  hasPrev: boolean;
  /**
   * 1-based position of the loaded group within the sequence, or 0 when there
   * is no sequence (the Cart).
   *
   * Surfaced so the panel can show the outer position alongside the per-group
   * clip counter. Without it "2 of 2 clips" reads as the end of the road even
   * though the arrow continues into the next group.
   */
  groupPosition: number;
  /** How many groups the sequence holds. 0 when there is no sequence. */
  groupCount: number;
  /**
   * True while a group step is resolving its clips. Normally false for long
   * enough to never be seen, since the groups on screen are already cached.
   */
  crossingGroup: boolean;
  openPreview: (args: OpenPreviewArgs) => void;
  /**
   * Remember a play as the most recently rendered accordion in the chat
   * without opening the preview. Used so the Clip Preview toggle can show the
   * latest play when the user hasn't clicked a specific row.
   */
  registerLatestPlay: (args: OpenPreviewArgs) => void;
  /**
   * Open the preview. If no clips are loaded yet, fall back to the most
   * recently registered play. With neither, the panel opens blank.
   */
  openLatest: () => void;
  closePreview: () => void;
  goToIndex: (index: number) => void;
  /**
   * Advance one clip. At the last clip of the group, steps into the next group
   * in the sequence and lands on its first clip. A no-op at the very end.
   */
  next: () => void;
  /**
   * Go back one clip. At the first clip of the group, steps into the previous
   * group and lands on its *last* clip, so the move is reversible.
   */
  prev: () => void;
}

const ClipPreviewContext = createContext<ClipPreviewContextType | undefined>(
  undefined
);

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const ClipPreviewProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clips, setClips] = useState<EnumeratedIconikAsset[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [gameId, setGameId] = useState("");
  const [playId, setPlayId] = useState("");
  const [source, setSource] = useState<ClipPreviewSource>("chat");
  const [sequence, setSequence] = useState<PreviewGroup[]>([]);
  const [groupKey, setGroupKey] = useState("");
  const [crossingGroup, setCrossingGroup] = useState(false);
  // Monotonic token identifying the current navigation intent.
  //
  // A group step awaits its clips, and anything that changes what the preview is
  // showing during that await supersedes it: clicking another row, or closing
  // the panel. Without this, the resolved group would write itself in on top of
  // wherever the user actually went. `crossingGroup` only disables the arrows;
  // it says nothing about the rest of the app.
  //
  // A ref rather than per-call state, deliberately: the check has to compare
  // against the *latest* intent at the moment the promise settles, which is
  // exactly what a ref reads. (`useGroupedPlayAssets` reaches the opposite
  // conclusion for its effect-scoped `cancelled` flag, where a shared ref would
  // be reset by the next run.)
  const crossTokenRef = useRef(0);

  /**
   * Invalidate any group step still in flight and release the arrows.
   *
   * Clearing `crossingGroup` here is not redundant: a superseded step returns
   * without clearing it (that belongs to whoever owns the current token), so if
   * nothing cleared it on this path the arrows would stay disabled for good.
   */
  const supersedeCrossing = useCallback(() => {
    crossTokenRef.current += 1;
    setCrossingGroup(false);
  }, []);
  // Held in a ref, not state: the latest play is only read imperatively by
  // openLatest and never rendered. Using state here would re-render every
  // context consumer each time an accordion mounts and registers itself.
  const latestPlayRef = useRef<OpenPreviewArgs | null>(null);

  const openPreview = useCallback(
    ({
      clips: nextClips,
      index,
      gameId: g,
      playId: p,
      source: s = "chat",
      sequence: seq = [],
      groupKey: gk = "",
    }: OpenPreviewArgs) => {
      // Whatever the user just opened wins over a group step still resolving.
      supersedeCrossing();
      setClips(nextClips);
      setActiveIndex(clamp(index, 0, Math.max(nextClips.length - 1, 0)));
      setGameId(g);
      setPlayId(p);
      setSource(s);
      setSequence(seq);
      setGroupKey(gk);
      setIsOpen(true);
    },
    [supersedeCrossing]
  );

  const registerLatestPlay = useCallback((args: OpenPreviewArgs) => {
    latestPlayRef.current = args;
  }, []);

  const openLatest = useCallback(() => {
    if (clips.length === 0 && latestPlayRef.current) {
      openPreview(latestPlayRef.current);
    } else {
      setIsOpen(true);
    }
  }, [clips.length, openPreview]);

  const closePreview = useCallback(() => {
    // Also invalidates an in-flight step, so reopening shows what the user last
    // chose rather than a group that resolved after they closed the panel.
    supersedeCrossing();
    setIsOpen(false);
  }, [supersedeCrossing]);

  // Clamp activeIndex whenever it or clips change by deriving the safe value.
  const safeIndex = clamp(activeIndex, 0, Math.max(clips.length - 1, 0));

  // Where the loaded group sits in the sequence. -1 when the caller supplied no
  // sequence (the Cart) or the key is not in it, which disables group stepping
  // and leaves navigation confined to `clips`.
  const groupIndex = useMemo(
    () => (groupKey ? sequence.findIndex((g) => g.key === groupKey) : -1),
    [sequence, groupKey]
  );

  const hasPrevGroup = groupIndex > 0;
  const hasNextGroup = groupIndex >= 0 && groupIndex < sequence.length - 1;

  const hasPrev = safeIndex > 0 || hasPrevGroup;
  const hasNext = safeIndex < clips.length - 1 || hasNextGroup;

  // 0 rather than 1 when the group is not in a sequence, so the panel can tell
  // "no outer position to show" apart from "first of one".
  const groupPosition = groupIndex >= 0 ? groupIndex + 1 : 0;
  const groupCount = sequence.length;

  /**
   * Walk the sequence from ``from`` in direction ``step`` and show the first
   * group that yields clips, landing on its first or last clip.
   *
   * It walks rather than taking the immediate neighbour because a group can
   * resolve to nothing — a fetch that fails, or a cache emptied by an admin
   * changing the preview fields. Stopping there would leave the arrow visibly
   * enabled but inert, so an empty group is stepped over instead.
   */
  const crossToGroup = useCallback(
    async (from: number, step: 1 | -1, land: "first" | "last") => {
      const token = crossTokenRef.current + 1;
      crossTokenRef.current = token;
      setCrossingGroup(true);
      try {
        for (let i = from; i >= 0 && i < sequence.length; i += step) {
          const group = sequence[i];
          let groupClips: EnumeratedIconikAsset[] = [];
          try {
            groupClips = await group.load();
          } catch (error) {
            // Treated as an empty group: skip it rather than stranding the user.
            console.error(error);
          }
          // Superseded while awaiting — the user clicked another row or closed
          // the panel. Writing now would drag them to a group they navigated
          // away from. The window is usually nil, because a group on screen is
          // already cached, but `clearAssetsCache` (an admin toggling
          // showInPreview) sends these loads back to the network, where
          // `/assets` has a measured p50 of 2.2s and a p99 near 23s.
          if (token !== crossTokenRef.current) return;
          if (groupClips.length === 0) continue;

          // Set the group-scoped state directly rather than going through
          // openPreview, so `source` and `sequence` are carried over untouched.
          setClips(groupClips);
          setActiveIndex(land === "first" ? 0 : groupClips.length - 1);
          setGameId(group.gameId);
          setPlayId(group.playId);
          setGroupKey(group.key);
          return;
        }
      } finally {
        // Only if this step is still the current one. A superseded step must not
        // re-enable the arrows on behalf of whatever replaced it.
        if (token === crossTokenRef.current) setCrossingGroup(false);
      }
    },
    [sequence]
  );

  const next = useCallback(() => {
    if (safeIndex < clips.length - 1) {
      setActiveIndex(safeIndex + 1);
      return;
    }
    if (hasNextGroup) void crossToGroup(groupIndex + 1, 1, "first");
  }, [safeIndex, clips.length, hasNextGroup, groupIndex, crossToGroup]);

  const prev = useCallback(() => {
    if (safeIndex > 0) {
      setActiveIndex(safeIndex - 1);
      return;
    }
    // Lands on the previous group's LAST clip, so stepping back across a
    // boundary returns you to where stepping forward would have come from.
    if (hasPrevGroup) void crossToGroup(groupIndex - 1, -1, "last");
  }, [safeIndex, hasPrevGroup, groupIndex, crossToGroup]);

  const boundedGoToIndex = useCallback(
    (index: number) => {
      setActiveIndex(clamp(index, 0, Math.max(clips.length - 1, 0)));
    },
    [clips.length]
  );

  const value = useMemo<ClipPreviewContextType>(
    () => ({
      isOpen,
      clips,
      activeIndex: safeIndex,
      activeClip: clips[safeIndex],
      gameId,
      playId,
      source,
      hasNext,
      hasPrev,
      groupPosition,
      groupCount,
      crossingGroup,
      openPreview,
      registerLatestPlay,
      openLatest,
      closePreview,
      goToIndex: boundedGoToIndex,
      next,
      prev,
    }),
    [
      isOpen,
      clips,
      safeIndex,
      gameId,
      playId,
      source,
      hasNext,
      hasPrev,
      groupPosition,
      groupCount,
      crossingGroup,
      openPreview,
      registerLatestPlay,
      openLatest,
      closePreview,
      boundedGoToIndex,
      next,
      prev,
    ]
  );

  return (
    <ClipPreviewContext.Provider value={value}>
      {children}
    </ClipPreviewContext.Provider>
  );
};

export const useClipPreview = (): ClipPreviewContextType => {
  const context = useContext(ClipPreviewContext);
  if (context === undefined) {
    throw new Error("useClipPreview must be used within a ClipPreviewProvider");
  }
  return context;
};
